import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "../../utils";
import { EmailTemplateShare } from "./types";

const emailTemplatesQueryKey = "email-templates";

// Delete an email template
async function deleteEmailTemplate(id: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from("email_templates").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEmailTemplate,
    onSuccess: () => {
      toast.success("Email template deleted successfully");
      void queryClient.invalidateQueries({ queryKey: [emailTemplatesQueryKey] });
    },
    onError: (error) => {
      toast.error(`Failed to delete template: ${error.message}`);
    },
  });
}

// Share an email template
async function shareEmailTemplate({
  templateId,
  userId,
}: {
  templateId: string;
  userId: string;
}): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase
    .from("email_template_shares")
    .insert([{ template_id: templateId, user_id: userId }]);
  if (error) throw new Error(error.message);
}

export function useShareEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: shareEmailTemplate,
    onSuccess: () => {
      toast.success("Email template shared successfully");
      // Potentially invalidate queries related to shares if there are any
    },
    onError: (error) => {
      toast.error(`Failed to share template: ${error.message}`);
    },
  });
}

// Unshare an email template
async function unshareEmailTemplate({
  templateId,
  userId,
}: {
  templateId: string;
  userId: string;
}): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase
    .from("email_template_shares")
    .delete()
    .eq("template_id", templateId)
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
}

// Fetch shares for a template.
// Uses the get_email_template_shares SECURITY DEFINER RPC rather than a direct
// table read: the email_template_shares SELECT policies only expose rows to the
// share *recipient* or an admin (an owner-select policy would recurse through
// email_templates' own policy). The RPC authorizes the caller as owner-or-admin
// internally, so a non-admin owner can see who they shared with.
async function fetchEmailTemplateShares(templateId: string): Promise<EmailTemplateShare[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("get_email_template_shares", {
    p_template_id: templateId,
  });
  if (error) throw new Error(error.message);
  const rows: EmailTemplateShare[] = data ?? [];
  return rows;
}

export function useEmailTemplateShares(templateId: string, enabled: boolean = true) {
  return useQuery<EmailTemplateShare[]>({
    queryKey: [emailTemplatesQueryKey, templateId, "shares"],
    queryFn: () => fetchEmailTemplateShares(templateId),
    enabled: enabled && !!templateId,
  });
}

export function useUnshareEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unshareEmailTemplate,
    onSuccess: () => {
      toast.success("Email template unshared successfully");
      // Potentially invalidate queries related to shares if there are any
    },
    onError: (error) => {
      toast.error(`Failed to unshare template: ${error.message}`);
    },
  });
}
